import { inject, Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { MarkDeliverableFilesActions } from '@aiwyn/api';

export { MarkDeliverableFilesActions };

export interface CxpDeliverableStaffResponse {
  id: number;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
}

/**
 * Raw shape returned by the CXP **list** GraphQL query for tasks.
 * Minimal projection: only the fields the task list mapper consumes,
 * including the per-file `viewedAt` timestamps used to compute task
 * completion.
 */
export interface CxpDeliverableTaskApiFile {
  fileId: number;
  viewedAt: string | null;
}

/**
 * Per-signer entry persisted on a SafeSend deliverable's `custom_field_values`.
 * Composed by the backend from `/signer-info` + `/access-link` and captured
 * once when the return reaches `AwaitingESign`.
 */
export interface SafeSendSigner {
  signerGuid: string;
  documentId: number;
  name: string;
  email: string;
  signerType: number;
  signerTypeLabel: string;
  order: number;
  isSigned: boolean;
  signedOn: string | null;
  deepLink: string;
}

export interface CxpDeliverableTaskApiResponse {
  id: number;
  name: string;
  description: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
  jobId: number | null;
  clientId: number | null;
  /** The deliverable's parent job, surfaced as `project` for the task card's meta line. */
  project: { name: string | null } | null;
  custom_field_values: {
    safesend_documentId?: number;
    /** Legacy single-link field. Pre-remake deliverables only. */
    safesend_deepLink?: string;
    safesend_signers?: SafeSendSigner[];
  } | null;
  deliverableFiles: CxpDeliverableTaskApiFile[];
}

/**
 * Post-filter shape: the API service drops null-clientId rows so the rest
 * of the pipeline can rely on clientId being present.
 */
export type CxpDeliverableTaskWithClient = CxpDeliverableTaskApiResponse & {
  clientId: number;
};

/**
 * Raw shape returned by the CXP single-deliverable GraphQL query.
 *
 * Mirrors the legacy `DeliverableModel` fields needed by the detail view, but
 * is owned by the CXP module so changes here do not affect the legacy
 * client-requests fetchers.
 */
export interface CxpDeliverableApiResponse {
  id: number;
  name: string;
  description: string;
  status: string;
  jobId: number | null;
  clientId: number | null;
  fileIds: { fileId: number }[];
  deliverableContacts: {
    contactId: number;
    contact: {
      id: number;
      username: string | null;
      contact_info: {
        full_name: string | null;
        email: string | null;
      };
    } | null;
  }[];
  /**
   * Firm staff who originally created/sent the deliverable. Maps to the
   * "Team" sidebar section per AC #15 ("regardless if it was the initial send
   * or a resend").
   */
  createdByStaff: CxpDeliverableStaffResponse | null;
  /**
   * Firm staff who most recently updated the deliverable (resender, if any).
   * Combined with `createdByStaff` to render the Team section; deduped by id
   * so a single sender doesn't appear twice.
   */
  lastUpdatedByStaff: CxpDeliverableStaffResponse | null;
}

@Injectable({ providedIn: 'root' })
export class CxpDeliverableApiService {
  private readonly apollo = inject(Apollo);

  /**
   * Fetches a single deliverable with the contact list (multi-contacts shape)
   * and selects the contact email alongside name. Used by the CXP detail view.
   */
  getDeliverableById(deliverableId: number): Observable<CxpDeliverableApiResponse | null> {
    return this.apollo
      .query<{ deliverables: CxpDeliverableApiResponse[] }>({
        query: gql`
          query GetCxpDeliverableById($deliverableId: Int!) {
            deliverables: vw_deliverable(where: { id: { _eq: $deliverableId } }, limit: 1) {
              id
              name
              description
              status
              jobId
              clientId
              fileIds: deliverableFiles {
                fileId
              }
              deliverableContacts {
                contactId
                contact {
                  id
                  username
                  contact_info {
                    full_name
                    email
                  }
                }
              }
              createdByStaff {
                id
                firstName
                lastName
                email
              }
              lastUpdatedByStaff {
                id
                firstName
                lastName
                email
              }
            }
          }
        `,
        variables: { deliverableId },
      })
      .pipe(map(({ data }) => data.deliverables[0] ?? null));
  }

  /**
   * Lists deliverables for the tasks page. Pulls the user-scoped per-file
   * `viewedAt` timestamps from `vw_deliverable_file` as a relation, which
   * the mapper aggregates into a `lastViewedAt` for completion logic.
   *
   * Assumes the multi-contacts feature flag is on (per CXP convention) —
   * filters via the `deliverableContacts` relation rather than the legacy
   * `contactId` column.
   */
  getDeliverablesForTasks(contactIds: number[]): Observable<CxpDeliverableTaskWithClient[]> {
    return this.apollo
      .query<{ deliverables: CxpDeliverableTaskApiResponse[] }>({
        query: gql`
          query GetCxpDeliverablesForTasks(
            $contactIds: [bigint!]!
            $excludeStatus: [String!]!
          ) {
            deliverables: vw_deliverable(
              where: {
                status: { _nin: $excludeStatus }
                deliverableContacts: { contactId: { _in: $contactIds } }
              }
            ) {
              id
              name
              description
              status
              createdAt
              updatedAt
              jobId
              clientId
              project: job {
                name
              }
              custom_field_values
              deliverableContacts { contactId }
              deliverableFiles {
                fileId
                viewedAt
              }
            }
          }
        `,
        variables: {
          contactIds: Array.from(new Set(contactIds)),
          excludeStatus: ['DRAFT'],
        },
      })
      .pipe(
        map(({ data }) =>
          // Defensive: drop any row If Hasura returns `clientId: null`.
          (data.deliverables ?? []).filter(
            (d): d is CxpDeliverableTaskWithClient => d.clientId !== null,
          ),
        ),
      );
  }

  /**
   * Records a per-file action (VIEWED or DOWNLOADED) against the legacy
   * `MarkDeliverableFiles` GraphQL mutation. Mirrors the call made by the
   * legacy deliverable component in `client-requests/components/deliverable`
   * — kept inside CXP so consumers don't reach into the legacy module.
   */
  markDeliverableFiles(
    deliverableId: number,
    fileIds: number[],
    action: MarkDeliverableFilesActions,
  ): Observable<boolean> {
    return this.apollo
      .mutate<{ MarkDeliverableFiles: { response: boolean } }>({
        mutation: gql`
          mutation ($markDeliverableFilesRequest: MarkDeliverableFilesRequest!) {
            MarkDeliverableFiles(markDeliverableFilesRequest: $markDeliverableFilesRequest) {
              response
            }
          }
        `,
        variables: {
          markDeliverableFilesRequest: {
            deliverableId,
            fileIds,
            action: action.toString(),
          },
        },
      })
      .pipe(map(({ data }) => data?.MarkDeliverableFiles?.response ?? false));
  }
}
